import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// 小组件同步导入（体积小，首屏需要）
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import Home from './components/Home';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import { NotificationProvider } from './contexts/NotificationContext';
import { UserQuotaProvider } from './contexts/UserQuotaContext';
import { UserDataProvider } from './contexts/UserDataContext';
import BugReportWidget from './components/BugReportWidget';

// 大组件懒加载（减少首屏加载体积）
const Dashboard = lazy(() => import('./components/Dashboard'));
const VideoGeneration = lazy(() => import('./components/VideoGeneration'));
const TaskManagement = lazy(() => import('./components/TaskManagement'));
const History = lazy(() => import('./components/History'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const VideoDownload = lazy(() => import('./components/VideoDownload'));
const VideoClone = lazy(() => import('./components/VideoClone'));
const ImageGeneration = lazy(() => import('./components/ImageGeneration'));
const ChatAndAnalysis = lazy(() => import('./components/ChatAndAnalysis'));
// const Settings = lazy(() => import('./components/Settings')); // 已弃用：线路选择已集成到视频生成页面
const Membership = lazy(() => import('./components/Membership'));
const ReferralCenter = lazy(() => import('./components/ReferralCenter'));
const CommissionManagement = lazy(() => import('./components/CommissionManagement'));
const Notifications = lazy(() => import('./components/Notifications'));
const Layout = lazy(() => import('./components/Layout'));
const VogueFitApp = lazy(() => import('./components/VogueFitApp'));
const ProductDesignStudio = lazy(() => import('./components/VogueFit/ProductDesignStudio'));
const Contact = lazy(() => import('./components/Contact'));

// 加载中占位组件
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-200 via-orange-200 via-pink-200 via-purple-200 via-rose-200 to-pink-300">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-pink-700 font-medium">加载中...</p>
    </div>
  </div>
);

function App() {
  console.log('App component rendering...');
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <Router>
        <BugReportWidget />
        <div className="min-h-screen">
          <Routes>
            {/* 精美首页 - 公开访问 */}
            <Route path="/" element={<Home />} />

            {/* 公开路由 - 保留独立登录页面 */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* 主应用路由 - 需要登录才能访问 */}
            <Route 
              path="/app" 
              element={
                <ProtectedRoute>
                  {/* 仅在受保护的应用区域内挂载用户数据上下文，避免公开页面触发刷新 */}
                  <UserDataProvider>
                    <UserQuotaProvider>
                      <Suspense fallback={<LoadingFallback />}>
                        <Layout />
                      </Suspense>
                    </UserQuotaProvider>
                  </UserDataProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense>} />
              <Route path="generate" element={<Suspense fallback={<LoadingFallback />}><VideoGeneration /></Suspense>} />
              <Route path="clone" element={<Suspense fallback={<LoadingFallback />}><VideoClone /></Suspense>} />
              <Route path="images" element={<Suspense fallback={<LoadingFallback />}><ImageGeneration /></Suspense>} />
              <Route path="chat" element={<Suspense fallback={<LoadingFallback />}><ChatAndAnalysis /></Suspense>} />
              <Route path="tasks" element={<Suspense fallback={<LoadingFallback />}><TaskManagement /></Suspense>} />
              <Route path="history" element={<Suspense fallback={<LoadingFallback />}><History /></Suspense>} />
              <Route path="download" element={<Suspense fallback={<LoadingFallback />}><VideoDownload /></Suspense>} />
              <Route path="profile" element={<Suspense fallback={<LoadingFallback />}><UserProfile /></Suspense>} />
              <Route path="membership" element={<Suspense fallback={<LoadingFallback />}><Membership /></Suspense>} />
              <Route path="referral" element={<Suspense fallback={<LoadingFallback />}><ReferralCenter /></Suspense>} />
              <Route path="referral/enable" element={<Suspense fallback={<LoadingFallback />}><ReferralCenter /></Suspense>} />
              <Route path="referral/management" element={<Suspense fallback={<LoadingFallback />}><CommissionManagement /></Suspense>} />
              <Route path="notifications" element={<Suspense fallback={<LoadingFallback />}><Notifications /></Suspense>} />
              <Route path="contact" element={<Suspense fallback={<LoadingFallback />}><Contact /></Suspense>} />
              <Route path="voguefit" element={<Suspense fallback={<LoadingFallback />}><VogueFitApp /></Suspense>} />
              <Route path="voguefit/design" element={<Suspense fallback={<LoadingFallback />}><ProductDesignStudio /></Suspense>} />
              {/* <Route path="settings" element={<Suspense fallback={<LoadingFallback />}><Settings /></Suspense>} /> */} {/* 已弃用：线路选择已集成到视频生成页面 */}
            </Route>

            {/* 向后兼容 - 重定向到新的路由 */}
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            <Route path="/generate" element={<Navigate to="/app/generate" replace />} />
            <Route path="/tasks" element={<Navigate to="/app/tasks" replace />} />
            <Route path="/history" element={<Navigate to="/app/history" replace />} />
            <Route path="/profile" element={<Navigate to="/app/profile" replace />} />

            {/* 默认重定向到首页 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </div>
        </Router>
      </NotificationProvider>
    </ErrorBoundary>
  );
}

export default App;
